import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AssistantPopover } from "./AssistantPopover";
import { ScreenRecorder } from "./ScreenRecorder";
import { List, SignOut, X, CaretDown, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ComplianceGate } from "./ComplianceGate";
import { TrainingRadarNotice } from "./TrainingRadarNotice";
import { QuickFind } from "./QuickFind";
import { Button } from "./ui/button";

const DAILY_LINKS = [
  { to: "/calendar", label: "Calendar & scheduling", testid: "nav-calendar" },
  { to: "/notes", label: "Notes", testid: "nav-notes" },
  { to: "/reports", label: "Reports", testid: "nav-reports-top" },
  { to: "/call-log", label: "Call log", testid: "nav-call-log" },
  { to: "/missed-calls", label: "Missed calls log", testid: "nav-missed-calls" },
  { to: "/overnight-review", label: "Overnight check-in review", testid: "nav-overnight-review" },
  { group: "Money", to: "/billing", label: "Billing & prepay", testid: "nav-billing" },
  { to: "/profitability", label: "Profitability (billing vs payroll)", testid: "nav-profitability" },
];
const EMPLOYEE_LINKS = [
  { group: "Files & records", to: "/employee-files", label: "Employee files (all records)", testid: "nav-employee-files" },
  { to: "/users", label: "Employee roster", testid: "nav-users" },
  { to: "/orientation", label: "Onboarding & orientation", testid: "nav-orientation" },
  { to: "/print-center", label: "Print center — ID badges & labels", testid: "nav-print-center" },
  { group: "Hiring", to: "/hiring", label: "Applicants (hiring pipeline)", testid: "nav-hiring" },
  { action: "copy-apply-link", label: "Send application link", testid: "nav-send-application" },
  { to: "/references", label: "References & verifications", testid: "nav-references" },
  { to: "/hiring-register", label: "Hiring register — dates & every reference", testid: "nav-hiring-register" },
  { to: "/referrals", label: "Referral bonuses", testid: "nav-referrals" },
  { group: "Team", to: "/meetings", label: "Meetings", testid: "nav-meetings" },
  { to: "/forms", label: "Forms", testid: "nav-forms-admin" },
  { to: "/admin/website-photos", label: "Website photos — staff consent", testid: "nav-website-photos" },
];
const CLIENT_LINKS = [
  { group: "The roster update project", to: "/paperwork-update", label: "Paperwork update — newest client first, track & print", testid: "nav-paperwork-update" },
  { to: "/clients", label: "Client list", testid: "nav-clients" },
  { group: "Bring on a client", to: "/clients/intake", label: "New client — start the intake", testid: "nav-new-client" },
  { action: "copy-intake-link", label: "Send intake link", testid: "nav-send-intake" },
  { group: "Records", to: "/careplan-records", label: "Care plan records — wet signatures", testid: "nav-careplan-records" },
  { to: "/reconciliation", label: "Visits & documents", testid: "nav-reconciliation" },
  { to: "/billing", label: "Client billing & prepay", testid: "nav-client-billing" },
  { to: "/client-handbook", label: "Client handbook", testid: "nav-client-handbook" },
];
const TRAINING_LINKS = [
  { group: "Staff training", to: "/training?tab=tracker", label: "Training tracker — who's due", testid: "nav-training-tracker" },
  { to: "/training?tab=topics", label: "Training library (all topics)", testid: "nav-training-library" },
  { to: "/training-tests", label: "Paper & electronic tests (modules 1–7)", testid: "nav-training-tests" },
  { to: "/training-base", label: "Training base (source materials)", testid: "nav-training-base" },
  { group: "Build training", to: "/training-creator", label: "Training creator (AI packs, voiceover, video)", testid: "nav-training-creator-menu" },
  { to: "/questions", label: "Training question bank", testid: "nav-question-bank" },
  { group: "Care task codes", to: "/task-codes", label: "Task codes hub (everything per code)", testid: "nav-task-codes" },
];
const COMPLIANCE_LINKS = [
  { to: "/compliance-directory", label: "Directory — who's flagged & why", testid: "nav-directory-top" },
  { group: "Policies & quality", to: "/policies", label: "Policies", testid: "nav-policies" },
  { to: "/guidelines", label: "Guidelines", testid: "nav-guidelines" },
  { to: "/quality", label: "Quality (CQI)", testid: "nav-quality" },
  { to: "/survey-answers", label: "Survey Answers", testid: "nav-survey-answers" },
  { to: "/complaints", label: "Complaints log", testid: "nav-complaints" },
  { to: "/incidents", label: "Incident log", testid: "nav-incidents" },
  { group: "Audits & EVV", to: "/audit", label: "Audit Prep", testid: "nav-audit" },
  { to: "/observations", label: "Observations & face-to-face", testid: "nav-observations" },
  { to: "/audit-questionnaire", label: "Audit questionnaire", testid: "nav-audit-questionnaire" },
  { to: "/payer-audits", label: "Payer Audits", testid: "nav-payer-audits" },
  { to: "/evv", label: "EVV", testid: "nav-evv" },
];
const SYSTEM_LINKS = [
  { group: "Office resources", to: "/documents", label: "Document library — every blank form, download & fill online", testid: "nav-documents" },
  { to: "/templates", label: "Employee form letters", testid: "nav-form-letters" },
  { to: "/handbook", label: "Employee handbook", testid: "nav-handbook" },
  { to: "/quick-reference", label: "Quick reference card", testid: "nav-quick-reference" },
  { to: "/knowledge", label: "Knowledge base", testid: "nav-knowledge-top" },
  { group: "Growth & marketing", to: "/competition", label: "Competition — see what's working", testid: "nav-competition" },
  { to: "/admin/reviews", label: "Reviews — approve & publish testimonials", testid: "nav-reviews" },
  { to: "/image-gallery", label: "Image picker — what fits the brand", testid: "nav-image-gallery" },
  { to: "/assistant", label: "Assistant", testid: "nav-assistant" },
  { group: "Setup", to: "/uploads", label: "Uploads — bulk files & scans", testid: "nav-uploads" },
  { to: "/setup-uploads", label: "Setup uploads — bulk imports & OCR backfill", testid: "nav-setup-uploads" },
  { to: "/notifications", label: "Notifications", testid: "nav-notifications" },
  { to: "/settings", label: "Settings", testid: "nav-settings" },
  { to: "/guide", label: "Guide", testid: "nav-guide" },
  { group: "Links", action: "caregiver-portal", label: "Caregiver portal", testid: "nav-caregiver-portal" },
  { action: "main-site", label: "← Main site (public website)", testid: "nav-main-site" },
];

const ADMIN_MENUS = [
  { label: "Daily", testid: "nav-daily-menu", links: DAILY_LINKS },
  { label: "Clients", testid: "nav-clients-menu", links: CLIENT_LINKS },
  { label: "Employees", testid: "nav-employees-menu", links: EMPLOYEE_LINKS },
  { label: "Training", testid: "nav-training-menu", links: TRAINING_LINKS },
  { label: "Compliance", testid: "nav-compliance-menu", links: COMPLIANCE_LINKS },
  { label: "Office", testid: "nav-system-menu", links: SYSTEM_LINKS },
];

function NavMenu({ label, testid, links }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  const copyLink = (path, msg) => {
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    } catch {}
    toast.success(msg, { description: url, duration: 8000 });
  };
  const runAction = async (action) => {
    if (action === "caregiver-portal") {
      try {
        await api.post("/auth/preview-login", { role: "caregiver" });
        window.location.href = "/dashboard";
      } catch { window.location.href = "/login"; }
    } else if (action === "main-site") {
      window.location.href = "/";
    } else {
      copyLink(action === "copy-apply-link" ? "/apply" : "/intake-start", action === "copy-apply-link" ? "Application link copied — paste it into a text or email" : "Intake link copied — paste it into a text or email");
    }
  };
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 rounded-full px-3 py-2 text-[13px] font-medium text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 xl:px-4 xl:text-sm"
        data-testid={testid}>
        {label} <CaretDown size={13} />
      </button>
      {open && (
        <>
          <div className="absolute left-0 z-50 mt-1 w-72 rounded-xl border border-stone-200 bg-white py-1 shadow-lg" data-testid={`${testid}-list`}>
            {links.map((l) => (
              <div key={l.testid}>
                {l.group && <p className="px-4 pb-0.5 pt-2.5 text-[10px] font-semibold uppercase tracking-wider text-stone-400">{l.group}</p>}
                {l.action ? (
                  <button onClick={() => { setOpen(false); runAction(l.action); }}
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-primary transition-colors hover:bg-accent" data-testid={l.testid}>
                    <LinkSimple size={14} /> {l.label}
                  </button>
                ) : (
                  <Link to={l.to} onClick={() => setOpen(false)}
                    className="block px-4 py-2 text-sm font-medium text-stone-700 transition-colors hover:bg-accent" data-testid={l.testid}>
                    {l.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function MobileNavItem({ l, close }) {
  const copyLink = (path, msg) => {
    const url = `${window.location.origin}${path}`;
    try {
      if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
    } catch {}
    toast.success(msg, { description: url, duration: 8000 });
  };
  if (l.action) {
    return (
      <button onClick={() => { close(); copyLink(l.action === "copy-apply-link" ? "/apply" : "/intake-start", `${l.label} — link copied`); }}
        className="flex w-full items-center gap-2 rounded-md px-3 py-2.5 text-left text-sm font-medium text-primary hover:bg-accent" data-testid={`mobile-${l.testid}`}>
        <LinkSimple size={14} /> {l.label}
      </button>
    );
  }
  return (
    <Link to={l.to} onClick={close}
      className="block rounded-md px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-accent" data-testid={`mobile-${l.testid}`}>
      {l.label}
    </Link>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAdmin = user?.role === "admin";
  const officeScopes = user?.role === "office" ? (user?.scopes || []) : [];
  if (localStorage.getItem("shc_view_staff") !== null) {
    if (sessionStorage.getItem("shc_view_staff") === null) sessionStorage.setItem("shc_view_staff", localStorage.getItem("shc_view_staff"));
    localStorage.removeItem("shc_view_staff");
  }
  const staffPreview = sessionStorage.getItem("shc_view_staff") === "1";
  const staffView = !isAdmin || staffPreview;

  const toggleStaffView = () => {
    sessionStorage.setItem("shc_view_staff", staffPreview ? "0" : "1");
    window.location.href = "/";
  };

  const handleLogout = async () => {
    sessionStorage.removeItem("shc_view_staff");
    localStorage.removeItem("shc_view_staff");
    await logout();
    window.location.href = "/";
  };

  const [cgBusy, setCgBusy] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (user?.role !== "admin") return;
    const path = location.pathname;
    if (!path || path === "/") return;
    const key = `pv:${path}:${new Date().toISOString().slice(0, 10)}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    api.post("/track/page-view", { path }).catch(() => {});
  }, [location.pathname, user?.role]); // eslint-disable-line
  const openCaregiverPortal = async (e) => {
    e.preventDefault();
    if (cgBusy) return;
    setCgBusy(true);
    try {
      await api.post("/auth/preview-login", { role: "caregiver" });
      window.location.href = "/dashboard";
      return;
    } catch { /* production: fall through to login */ }
    setCgBusy(false);
    window.location.href = "/login";
  };

  const staffLinks = [
    { to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" },
    { to: "/field", label: "Field tools", testid: "nav-field" },
    { to: "/quick-reference", label: "Quick reference", testid: "nav-quick-reference" },
    { to: "/handbook", label: "Handbook", testid: "nav-handbook" },
    { to: "/swag-shop", label: "Swag Shop", testid: "nav-swag-shop" },
    { to: "/wrap-wheel", label: "Prize wheel", testid: "nav-wrap-wheel" },
    { to: "/designated-caregiver", label: "Designated caregiver", testid: "nav-designated-caregiver" },
  ];

  const officeLinks = [
    ...(officeScopes.includes("references") ? [{ to: "/references", label: "References & verifications", testid: "nav-references" }] : []),
    ...(officeScopes.includes("careplans") ? [{ to: "/clients", label: "Clients — care plans & intakes", testid: "nav-clients" }] : []),
    { to: "/profile", label: "My Profile", testid: "nav-profile" },
  ];

  const linkCls = "rounded-full px-3 py-2 text-[13px] font-medium text-stone-600 transition-colors hover:bg-stone-100 hover:text-stone-900 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 xl:px-4 xl:text-sm";

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-white/40 bg-white/70 shadow-[0_8px_32px_rgba(0,0,0,0.04)] backdrop-blur-xl backdrop-saturate-150">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-3 px-6">
          <Link to={officeScopes.length ? officeLinks[0].to : "/dashboard"} className="flex shrink-0 items-center gap-2" data-testid="app-logo-link" onClick={() => setMenuOpen(false)}>
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-11 w-auto" />
          </Link>
          {!staffView && <QuickFind />}
          <div className="flex items-center gap-3">
            <nav className="hidden items-center gap-0.5 lg:flex">
              {(officeScopes.length ? officeLinks : staffView ? staffLinks : [{ to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" }]).map((l) => (
                <Link key={l.to} to={l.to} className={linkCls} data-testid={l.testid}>{l.label}</Link>
              ))}
              {!staffView && ADMIN_MENUS.map((m) => (
                <NavMenu key={m.testid} label={m.label} testid={m.testid} links={m.links} />
              ))}
              <Link to="/profile" className={linkCls} data-testid="nav-profile">My Profile</Link>
            </nav>
            {isAdmin && (
              <button onClick={toggleStaffView} data-testid="toggle-employee-view"
                className={`hidden rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors sm:inline-flex ${staffPreview ? "border-[#B7791F]/50 bg-[#B7791F]/15 text-[#8a5d14]" : "border-stone-300 text-stone-500 hover:bg-stone-900 hover:text-white"}`}>
                {staffPreview ? "Exit employee view" : "View as employee"}
              </button>
            )}
            <span className="hidden text-sm text-stone-500 xl:block" data-testid="header-user-email">{user?.email}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-button"
              className="rounded-full border-stone-300 text-stone-700 transition-colors hover:bg-stone-900 hover:text-white active:scale-95">
              <SignOut size={16} className="sm:mr-1.5" /> <span className="hidden sm:inline">Sign out</span>
            </Button>
            {staffView && <div className="md:hidden"><QuickFind /></div>}
            <button onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="mobile-menu-button"
              className="flex h-9 w-9 items-center justify-center rounded-md border border-stone-300 text-stone-700 hover:bg-accent lg:hidden">
              {menuOpen ? <X size={20} /> : <List size={20} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav className="max-h-[70vh] overflow-y-auto border-t border-white/40 bg-white/80 px-4 py-2 backdrop-blur-xl lg:hidden" data-testid="mobile-nav">
            {isAdmin && (
              <button onClick={toggleStaffView} data-testid="mobile-toggle-employee-view"
                className={`mb-2 w-full rounded-lg border px-3 py-2 text-sm font-semibold ${staffPreview ? "border-[#B7791F]/50 bg-[#B7791F]/15 text-[#8a5d14]" : "border-stone-300 text-stone-600"}`}>
                {staffPreview ? "Exit employee view" : "View as employee"}
              </button>
            )}
            {staffView ? (
              <>
                {[...(officeScopes.length ? officeLinks : staffLinks), ...(officeScopes.length ? [] : [{ to: "/profile", label: "My Profile", testid: "nav-profile" }])].map((l) => (
                  <MobileNavItem key={l.to} l={l} close={() => setMenuOpen(false)} />
                ))}
              </>
            ) : (
              <>
                <MobileNavItem l={{ to: "/dashboard", label: "Dashboard", testid: "nav-dashboard" }} close={() => setMenuOpen(false)} />
                <MobileNavItem l={{ to: "/profile", label: "My Profile", testid: "nav-profile" }} close={() => setMenuOpen(false)} />
                {user?.role === "admin" && (
                  <a href="/dashboard" onClick={(e) => { setMenuOpen(false); openCaregiverPortal(e); }}
                    className="block rounded-lg px-3 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-100" data-testid="mobile-nav-caregiver-portal">
                    Caregiver portal
                  </a>
                )}
                <a href="/" onClick={() => setMenuOpen(false)} className="block rounded-lg px-3 py-2.5 text-sm font-medium text-stone-600 hover:bg-stone-100" data-testid="mobile-nav-main-site">← Main site</a>
                {ADMIN_MENUS.map((m) => (
                  <div key={m.testid}>
                    <p className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-stone-400">{m.label}</p>
                    {m.links.map((l) => (
                      <div key={l.testid}>
                        {l.group && <p className="px-3 pb-0.5 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-stone-300">{l.group}</p>}
                        <MobileNavItem l={l} close={() => setMenuOpen(false)} />
                      </div>
                    ))}
                  </div>
                ))}
              </>
            )}
          </nav>
        )}
      </header>
      {isAdmin && staffPreview && (
        <div className="border-b border-[#B7791F]/30 bg-[#B7791F]/15 px-6 py-2 text-center text-xs font-medium text-[#8a5d14]" data-testid="employee-view-banner">
          You're previewing the employee experience — your admin access is unchanged.{" "}
          <button onClick={toggleStaffView} className="font-semibold underline" data-testid="exit-employee-view">Exit</button>
        </div>
      )}
      <ComplianceGate />
      <TrainingRadarNotice />
      <main className="mx-auto max-w-7xl px-6 py-8">{children}</main>
      {window.location.hostname.includes("preview") && (
        <span className="fixed bottom-3 left-1/2 z-50 -translate-x-1/2 rounded-full border border-[#B7791F]/50 bg-[#B7791F]/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-[#8a5d14] backdrop-blur-sm" data-testid="env-preview-badge">
          Preview — not live
        </span>
      )}
      {isAdmin && !staffPreview && <AssistantPopover />}
      {isAdmin && !staffPreview && <ScreenRecorder />}
    </div>
  );
}
